import { Alert, Box, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, Skeleton, Stack, TextField, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { MaterialReactTable, MRT_ExpandedState } from "material-react-table";
import { useSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import Page from "../../../minimals/components/page";
import { useUserContext } from "../../contexts/UserContext";
import { InternalUser } from "../../types/app";
import { QaPendingOrder, QaState } from "./types";
import { useQaPending } from "./useQaPending";
import QaOrderDetails from "./QaOrderDetails";
import { qaReviewColumns } from "./qaReviewColumns";

export default function PendingQaOrders() {
  const qa = useQaPending();
  const { user } = useUserContext();
  const { enqueueSnackbar } = useSnackbar();
  const [params] = useSearchParams();
  const closerId = params.get("closerId");
  const [search, setSearch] = useState("");
  const [qaNoteFilter, setQaNoteFilter] = useState("");
  const [assignedToMeAsTme, setAssignedToMeAsTme] = useState(false);
  const [tmeCloserIds, setTmeCloserIds] = useState<Set<number> | null>(null);
  const [assignmentError, setAssignmentError] = useState(false);
  const [assignmentReload, setAssignmentReload] = useState(0);
  const [reviewing, setReviewing] = useState<Set<string>>(new Set());
  const reviewingRef = useRef(new Set<string>());
  const detailRevisions = useRef<Record<string, number>>({});
  const [expanded, setExpanded] = useState<MRT_ExpandedState>({});

  useEffect(() => {
    let cancelled = false;
    setTmeCloserIds(null);
    setAssignmentError(false);
    if (user?.id && qa.canAccess) {
      axios.get<InternalUser[]>("/api/clientphonebook/internalusers/getInternalUsersAssignedToTme", {
        params: { assignedToTmeId: user.id }
      }).then(({ data }) => {
        if (!cancelled) setTmeCloserIds(new Set(data.map((closer) => closer.id)));
      }).catch(() => {
        if (!cancelled) setAssignmentError(true);
      });
    }
    return () => { cancelled = true; };
  }, [user?.id, qa.canAccess, assignmentReload]);

  const scoped = qa.orders.filter((order) => closerId === null || String(order.closerId ?? -1) === closerId);
  const noteSearch = qaNoteFilter.trim().toLocaleLowerCase();
  const orders = scoped.filter((order) => (!assignedToMeAsTme ||
    (order.closerId !== null && tmeCloserIds?.has(order.closerId))) &&
    (!noteSearch || order.qaNoteTexts?.some((note) => note.toLocaleLowerCase().includes(noteSearch))));

  const onDetailsLoaded = useCallback((orderNumber: string, state: QaState) => {
    detailRevisions.current[orderNumber] = state.revision;
  }, []);

  const refresh = async () => {
    setExpanded({});
    detailRevisions.current = {};
    setAssignmentReload((value) => value + 1);
    await qa.reload();
  };

  const reviewOrder = async (order: QaPendingOrder) => {
    if (reviewingRef.current.has(order.orderNumber)) return;
    reviewingRef.current.add(order.orderNumber);
    setReviewing(new Set(reviewingRef.current));
    try {
      await axios.post("/api/ClosingChecklistQa/Review", {
        orderNumber: order.orderNumber,
        revision: detailRevisions.current[order.orderNumber] ?? order.revision
      });
      enqueueSnackbar(`${order.orderNumber} marked as QA reviewed.`, { variant: "success" });
      await qa.reload();
    } catch (error) {
      const stale = axios.isAxiosError(error) && [400, 409].includes(error.response?.status || 0);
      enqueueSnackbar(stale
        ? "QA findings have changed. The list has been refreshed; expand the order to review the latest notes."
        : "Unable to mark this order reviewed. Please try again.", { variant: "error" });
      if (stale) {
        delete detailRevisions.current[order.orderNumber];
        setExpanded((current) => current === true ? {} : { ...current, [order.orderNumber]: false });
        await qa.reload();
      }
    } finally {
      reviewingRef.current.delete(order.orderNumber);
      setReviewing(new Set(reviewingRef.current));
    }
  };

  return <Page title="Pending QA"><Box sx={{ p: 3 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
      <Typography variant="h4">Pending QA{closerId !== null && scoped[0]?.closerName ? ` · ${scoped[0].closerName}` : ""}</Typography>
      <Button onClick={refresh} disabled={qa.loading}>Refresh</Button>
    </Stack>
    {qa.error && <Alert severity="error" sx={{ mb: 2 }}>Unable to load QA findings. Please refresh to try again.</Alert>}
    {qa.loading && !qa.canAccess ? <Skeleton height={180} /> : !qa.canAccess ?
      !qa.error && <Alert severity="info">QA is available to the Policy Team and TMEs.</Alert> : <>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: "center" }}>
        <FormControlLabel
          label="Assigned to me as TME"
          control={<Checkbox checked={assignedToMeAsTme} disabled={tmeCloserIds === null}
            onChange={(_, checked) => setAssignedToMeAsTme(checked)} />}
        />
        <TextField label="Filter by QA note" placeholder="Search QA note text" size="small"
          value={qaNoteFilter} onChange={(event) => setQaNoteFilter(event.target.value)}
          helperText="Shows matching QA notes within each order."
          InputProps={{
            endAdornment: qaNoteFilter ? <InputAdornment position="end">
              <IconButton size="small" edge="end" aria-label="Clear QA note filter"
                onClick={() => setQaNoteFilter("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment> : null
          }}
          sx={{ width: { xs: "100%", sm: 320 } }} />
      </Stack>
      {assignmentError && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => setAssignmentReload((value) => value + 1)}>Retry</Button>}>
        Unable to load your TME assignments.
      </Alert>}
      <MaterialReactTable
        columns={qaReviewColumns}
        data={orders}
        getRowId={(order) => order.orderNumber}
        enableStickyHeader
        enableColumnFilters
        enableGlobalFilter
        globalFilterFn="includesString"
        enableRowActions
        positionActionsColumn="last"
        enableExpandAll={false}
        getRowCanExpand={() => true}
        initialState={{
          density: "compact",
          pagination: { pageIndex: 0, pageSize: 30 },
          showColumnFilters: true,
          showGlobalFilter: true,
          sorting: [{ id: "updatedAt", desc: true }]
        }}
        state={{ expanded, globalFilter: search, showProgressBars: qa.loading }}
        onExpandedChange={setExpanded}
        onGlobalFilterChange={setSearch}
        muiSearchTextFieldProps={{ placeholder: "Search orders", variant: "outlined", size: "small" }}
        muiTableBodyCellProps={{ sx: { verticalAlign: "top" } }}
        muiTableContainerProps={{ sx: { maxHeight: "calc(100vh - 290px)" } }}
        displayColumnDefOptions={{ "mrt-row-actions": { header: "Actions", size: 155 } }}
        renderRowActions={({ row }) => <LoadingButton
          size="small"
          variant="outlined"
          loading={reviewing.has(row.original.orderNumber)}
          disabled={qa.loading}
          aria-label={`Mark ${row.original.orderNumber} QA reviewed`}
          onClick={() => reviewOrder(row.original)}
        >QA Reviewed</LoadingButton>}
        renderDetailPanel={({ row }) => row.getIsExpanded() ? <QaOrderDetails
          orderNumber={row.original.orderNumber}
          revision={row.original.revision}
          noteSearch={noteSearch}
          onLoaded={onDetailsLoaded}
        /> : null}
        localization={{ noRecordsToDisplay: "No pending QA orders match your filters." }}
      />
    </>}
  </Box></Page>;
}
